import {
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

import FieldOptionRepository from "../../../../services/repositories/field-option-repository";
import ModalShell from "../../../global/ModalShell";
import {
  ContainedRoundedButton,
  OutlinedRoundedButton,
} from "../../../styled/buttons/SecondaryRoundedButton";

import AlternativeProviderItem from "./components/AlternativeProviderItem";

const AlternativeProvidersModal = ({
  open,
  onClose,
  onSelectAlternative,
  onConfirmOriginalProvider,
  alternativesData,
  isLoading = false,
}) => {
  const [selectedJustification, setSelectedJustification] = useState("");
  const [justificationOptions, setJustificationOptions] = useState([]);
  const [loadingJustification, setLoadingJustification] = useState(false);

  useEffect(() => {
    if (open) {
      setLoadingJustification(true);
      const fieldOptionRepository = new FieldOptionRepository();

      fieldOptionRepository
        .query({
          content_type: "providerclientstatus",
          field_name: "out_of_network_justification",
          flat: true,
        })
        .then((response) => {
          setJustificationOptions(response || []);
        })
        .catch((error) => {
          console.error("Error fetching justification options:", error);
        })
        .finally(() => {
          setLoadingJustification(false);
        });
    }
  }, [open]);

  const handleSelectProvider = (providerId) => {
    onSelectAlternative(providerId);
  };

  const handleConfirmOriginal = () => {
    if (!selectedJustification) {
      return;
    }
    onConfirmOriginalProvider(selectedJustification);
  };

  const handleClose = () => {
    setSelectedJustification("");
    onClose();
  };

  return (
    <ModalShell
      open={open}
      setOpen={(val) => !val && handleClose()}
      title="Add Provider"
      customWidth="lg"
      fullWidth
    >
      <div className="w-full max-w-none">
        {alternativesData?.header && (
          <div className="mb-6">
            <Typography variant="body1" className="whitespace-pre-line text-gray-600">
              {alternativesData.header}
            </Typography>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <CircularProgress />
          </div>
        ) : (
          <div className="mb-6">
            <div className="mb-4 grid grid-cols-5 gap-4 border-b border-gray-300 pb-2">
              <Typography variant="subtitle2" className="font-semibold">
                Provider Name
              </Typography>
              <Typography variant="subtitle2" className="font-semibold">
                Address
              </Typography>
              <Typography variant="subtitle2" className="font-semibold">
                Type
              </Typography>
              <Typography variant="subtitle2" className="font-semibold">
                Distance from Client
              </Typography>
              <Typography variant="subtitle2" className="text-center font-semibold">
                Action
              </Typography>
            </div>

            {alternativesData?.alternative_providers?.map((provider, index) => (
              <AlternativeProviderItem
                key={provider.provider_id || index}
                provider={provider}
                onSelect={() => handleSelectProvider(provider.provider_id)}
              />
            ))}
          </div>
        )}

        {alternativesData?.footer && (
          <div className="mb-6">
            <Typography variant="body1" className="whitespace-pre-line text-gray-600">
              {alternativesData.footer}
            </Typography>
          </div>
        )}

        <div className="mb-6">
          <Typography variant="subtitle1" className="mb-3 font-semibold">
            Why are you Choosing this Provider?
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="justification-select-label">Select an Option</InputLabel>
            <Select
              labelId="justification-select-label"
              value={selectedJustification}
              label="Select an Option"
              onChange={(e) => setSelectedJustification(e.target.value)}
              disabled={loadingJustification}
            >
              {justificationOptions.map(({ key, value }, index) => (
                <MenuItem key={index} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        <div className="flex w-full gap-4">
          <OutlinedRoundedButton onClick={handleClose} className="flex-1 normal-case">
            Cancel
          </OutlinedRoundedButton>
          <ContainedRoundedButton
            onClick={handleConfirmOriginal}
            disabled={!selectedJustification}
            className="flex-1 normal-case"
          >
            Confirm New Provider
          </ContainedRoundedButton>
        </div>
      </div>
    </ModalShell>
  );
};

AlternativeProvidersModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectAlternative: PropTypes.func.isRequired,
  onConfirmOriginalProvider: PropTypes.func.isRequired,
  alternativesData: PropTypes.shape({
    header: PropTypes.string,
    footer: PropTypes.string,
    alternative_providers: PropTypes.arrayOf(
      PropTypes.shape({
        provider_id: PropTypes.number,
        provider_name: PropTypes.string,
        provider_address: PropTypes.string,
        provider_types: PropTypes.string,
        distance: PropTypes.string,
      })
    ),
  }),
  isLoading: PropTypes.bool,
};

export default AlternativeProvidersModal;
